import { Mic, Brain, TrendingUp, Users, ArrowRight, CheckCircle2, Star, Sparkles, MessageSquare, Play, Sprout } from 'lucide-react';
import { useState } from 'react';
import DemoModal from './DemoModal';

interface LandingPageProps {
  onGetStarted: () => void;
}

export default function LandingPage({ onGetStarted }: LandingPageProps) {
  const [showDemo, setShowDemo] = useState(false);
  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <header className="container mx-auto px-6 py-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="text-[#8CAFA8]">
              <Sprout className="w-7 h-7" strokeWidth={1.5} />
            </div>
            <span className="text-2xl font-semibold text-[#1F2937]">Mentra</span>
          </div>
          <div className="flex items-center gap-4">
            <a href="/pricing" className="text-[#6B7280] hover:text-[#1F2937] font-medium hidden sm:inline transition">
              Pricing
            </a>
            <button
              onClick={onGetStarted}
              className="bg-[#8CAFA8] text-white px-6 py-2.5 rounded-[12px] font-medium hover:bg-[#7A9B94] transition shadow-[0_2px_8px_rgba(140,175,168,0.25)]"
            >
              Get Started
            </button>
          </div>
        </div>
      </header>

      <main>
        <section className="container mx-auto px-6 py-16">
          <div className="grid md:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            <div>
              <p className="text-base font-medium text-[#8CAFA8] mb-4 tracking-wide">
                Mentra, the AI-Driven 360° Feedback Platform for High-Performing Teams
              </p>
              <h1 className="text-5xl md:text-6xl font-semibold text-[#1F2937] mb-6 leading-tight">
                Grow your people.<br />
                Grow your teams.
              </h1>
              <p className="text-xl text-[#6B7280] mb-10 leading-relaxed">
                Capture voice and text feedback in seconds.
                Let AI do the heavy lifting with summaries, insights, and growth actions — all in one beautifully simple platform.
                <span className="block mt-3 font-medium text-[#1F2937]">Continuous feedback. Actionable insights. Real growth.</span>
              </p>

              <div className="flex flex-col sm:flex-row items-start gap-4 mb-8">
                <button
                  onClick={onGetStarted}
                  className="bg-[#8CAFA8] text-white px-8 py-4 rounded-[16px] font-medium text-lg hover:bg-[#7A9B94] transition shadow-[0_2px_12px_rgba(140,175,168,0.3)]"
                >
                  Start Free Trial
                </button>
                <button
                  onClick={() => setShowDemo(true)}
                  className="bg-white text-[#6B7280] px-8 py-4 rounded-[16px] font-medium text-lg hover:bg-gray-50 transition border border-gray-200 shadow-[0_2px_8px_rgba(0,0,0,0.04)]"
                >
                  Try Demo
                </button>
              </div>

              <div className="flex flex-col gap-3 text-sm text-[#6B7280]">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-[#8CAFA8]" strokeWidth={1.5} />
                  <span>Free to start</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-[#8CAFA8]" strokeWidth={1.5} />
                  <span>No credit card required</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-[#8CAFA8]" strokeWidth={1.5} />
                  <span>5 requests free per month</span>
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="bg-white rounded-[28px] shadow-[0_4px_24px_rgba(0,0,0,0.06)] overflow-hidden">
                <img
                  src="/Hero Shot.png"
                  alt="Mentra Dashboard"
                  className="w-full h-auto"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="container mx-auto px-6 py-20">
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <div className="bg-white p-8 rounded-[24px] shadow-[0_2px_12px_rgba(0,0,0,0.04)]">
              <div className="text-[#8CAFA8] mb-6">
                <Mic className="w-10 h-10" strokeWidth={1.5} />
              </div>
              <h3 className="text-xl font-semibold text-[#1F2937] mb-3">
                Voice-First Feedback
              </h3>
              <p className="text-[#6B7280] leading-relaxed">
                Colleagues share feedback by speaking for 3-5 minutes.
                No typing, no surveys, just authentic conversation.
              </p>
            </div>

            <div className="bg-white p-8 rounded-[24px] shadow-[0_2px_12px_rgba(0,0,0,0.04)]">
              <div className="text-[#8CAFA8] mb-6">
                <Brain className="w-10 h-10" strokeWidth={1.5} />
              </div>
              <h3 className="text-xl font-semibold text-[#1F2937] mb-3">
                AI-Powered Analysis
              </h3>
              <p className="text-[#6B7280] leading-relaxed">
                Advanced AI analyzes responses to identify patterns, strengths,
                and development opportunities across your feedback.
              </p>
            </div>

            <div className="bg-white p-8 rounded-[24px] shadow-[0_2px_12px_rgba(0,0,0,0.04)]">
              <div className="text-[#8CAFA8] mb-6">
                <TrendingUp className="w-10 h-10" strokeWidth={1.5} />
              </div>
              <h3 className="text-xl font-semibold text-[#1F2937] mb-3">
                Actionable Insights
              </h3>
              <p className="text-[#6B7280] leading-relaxed">
                Get personalized recommendations and a comprehensive view
                of how others see your strengths and potential.
              </p>
            </div>
          </div>
        </section>


        <section className="container mx-auto px-6 py-20">
          <div className="bg-white rounded-[28px] p-12 md:p-16 text-center shadow-[0_4px_24px_rgba(0,0,0,0.06)] max-w-4xl mx-auto">
            <div className="text-[#8CAFA8] mb-6 flex justify-center">
              <Play className="w-16 h-16" strokeWidth={1.5} />
            </div>
            <h2 className="text-4xl md:text-5xl font-semibold text-[#1F2937] mb-6">
              Try the Interactive Demo
            </h2>
            <p className="text-xl text-[#6B7280] mb-8 max-w-2xl mx-auto leading-relaxed">
              Experience Mentra in just 4 minutes. Answer three questions with your voice
              and see how our AI generates personalized insights about your professional strengths.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-6 mb-8">
              <div className="flex items-center gap-2 text-[#6B7280]">
                <CheckCircle2 className="w-5 h-5 text-[#8CAFA8]" strokeWidth={1.5} />
                <span>4 minute demo</span>
              </div>
              <div className="flex items-center gap-2 text-[#6B7280]">
                <CheckCircle2 className="w-5 h-5 text-[#8CAFA8]" strokeWidth={1.5} />
                <span>No installation needed</span>
              </div>
              <div className="flex items-center gap-2 text-[#6B7280]">
                <CheckCircle2 className="w-5 h-5 text-[#8CAFA8]" strokeWidth={1.5} />
                <span>See real AI in action</span>
              </div>
            </div>

            <button
              onClick={() => setShowDemo(true)}
              className="bg-[#8CAFA8] text-white px-10 py-5 rounded-[16px] font-medium text-xl hover:bg-[#7A9B94] transition inline-flex items-center gap-3 shadow-[0_4px_16px_rgba(140,175,168,0.3)]"
            >
              <Play className="w-6 h-6" strokeWidth={1.5} />
              Launch Interactive Demo
            </button>

            <p className="text-sm text-[#6B7280] mt-6">
              Turn on your microphone and speakers for the best experience
            </p>
          </div>
        </section>

        <section className="container mx-auto px-6 py-20">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-semibold text-[#1F2937] mb-4">
                How It Works
              </h2>
              <p className="text-[#6B7280] text-lg">
                Three simple steps to transform your professional growth
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white rounded-[24px] p-8 shadow-[0_2px_12px_rgba(0,0,0,0.04)]">
                <div className="w-12 h-12 rounded-[12px] bg-[#F8F9FA] flex items-center justify-center mb-4 text-2xl font-semibold text-[#1F2937]">
                  1
                </div>
                <h4 className="font-semibold text-lg text-[#1F2937] mb-2">Request Feedback</h4>
                <p className="text-[#6B7280] leading-relaxed">
                  Send personalized requests to colleagues via email with unique links.
                </p>
              </div>

              <div className="bg-white rounded-[24px] p-8 shadow-[0_2px_12px_rgba(0,0,0,0.04)]">
                <div className="w-12 h-12 rounded-[12px] bg-[#F8F9FA] flex items-center justify-center mb-4 text-2xl font-semibold text-[#1F2937]">
                  2
                </div>
                <h4 className="font-semibold text-lg text-[#1F2937] mb-2">They Share Insights</h4>
                <p className="text-[#6B7280] leading-relaxed">
                  Colleagues record 3-5 minutes of honest feedback using voice or text.
                </p>
              </div>

              <div className="bg-white rounded-[24px] p-8 shadow-[0_2px_12px_rgba(0,0,0,0.04)]">
                <div className="w-12 h-12 rounded-[12px] bg-[#F8F9FA] flex items-center justify-center mb-4 text-2xl font-semibold text-[#1F2937]">
                  3
                </div>
                <h4 className="font-semibold text-lg text-[#1F2937] mb-2">Get AI Insights</h4>
                <p className="text-[#6B7280] leading-relaxed">
                  Our AI analyzes feedback to surface themes, strengths, and recommendations.
                </p>
              </div>
            </div>

            <div className="text-center mt-12">
              <button
                onClick={onGetStarted}
                className="bg-[#8CAFA8] text-white px-8 py-4 rounded-[16px] font-medium text-lg hover:bg-[#7A9B94] transition inline-flex items-center gap-2 shadow-[0_4px_16px_rgba(140,175,168,0.3)]"
              >
                Start Your Free Trial
                <ArrowRight className="w-5 h-5" strokeWidth={1.5} />
              </button>
            </div>
          </div>
        </section>

        <section className="container mx-auto px-6 py-20">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-semibold text-[#1F2937] mb-4">
              Trusted by Professionals Worldwide
            </h2>
            <p className="text-xl text-[#6B7280]">
              Join thousands using Mentra for career development
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <div className="bg-white rounded-[24px] p-8 shadow-[0_2px_12px_rgba(0,0,0,0.04)]">
              <div className="flex gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 fill-[#8CAFA8] text-[#8CAFA8]" strokeWidth={0} />
                ))}
              </div>
              <p className="text-[#6B7280] mb-4 leading-relaxed">
                "Mentra helped me understand my blind spots and grow as a leader.
                The AI insights were spot-on and actionable."
              </p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#F8F9FA] rounded-full flex items-center justify-center">
                  <span className="text-[#1F2937] font-medium text-sm">SK</span>
                </div>
                <div>
                  <div className="font-medium text-[#1F2937]">Sarah Kim</div>
                  <div className="text-sm text-[#6B7280]">Product Manager</div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-[24px] p-8 shadow-[0_2px_12px_rgba(0,0,0,0.04)]">
              <div className="flex gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 fill-[#8CAFA8] text-[#8CAFA8]" strokeWidth={0} />
                ))}
              </div>
              <p className="text-[#6B7280] mb-4 leading-relaxed">
                "The voice feedback feature is genius. People are much more candid
                when speaking versus typing. Game changer!"
              </p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#F8F9FA] rounded-full flex items-center justify-center">
                  <span className="text-[#1F2937] font-medium text-sm">MR</span>
                </div>
                <div>
                  <div className="font-medium text-[#1F2937]">Marcus Rodriguez</div>
                  <div className="text-sm text-[#6B7280]">Engineering Lead</div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-[24px] p-8 shadow-[0_2px_12px_rgba(0,0,0,0.04)]">
              <div className="flex gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 fill-[#8CAFA8] text-[#8CAFA8]" strokeWidth={0} />
                ))}
              </div>
              <p className="text-[#6B7280] mb-4 leading-relaxed">
                "We use Mentra for our entire team. The 360 feedback cycles
                have dramatically improved our culture and performance."
              </p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#F8F9FA] rounded-full flex items-center justify-center">
                  <span className="text-[#1F2937] font-medium text-sm">JC</span>
                </div>
                <div>
                  <div className="font-medium text-[#1F2937]">Jessica Chen</div>
                  <div className="text-sm text-[#6B7280]">VP of People</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="container mx-auto px-6 py-20 text-center">
          <div className="max-w-3xl mx-auto">
            <div className="text-[#8CAFA8] flex justify-center mb-6">
              <Sprout className="w-16 h-16" strokeWidth={1.5} />
            </div>
            <h2 className="text-4xl font-semibold text-[#1F2937] mb-6">
              Ready to Accelerate Your Career?
            </h2>
            <p className="text-xl text-[#6B7280] mb-10 leading-relaxed">
              Join professionals who are using AI-powered feedback to unlock
              their potential and build better careers.
            </p>
            <button
              onClick={onGetStarted}
              className="bg-[#8CAFA8] text-white px-8 py-4 rounded-[16px] font-medium text-lg hover:bg-[#7A9B94] transition inline-flex items-center gap-2 shadow-[0_4px_16px_rgba(140,175,168,0.3)]"
            >
              Start Free Trial
              <ArrowRight className="w-5 h-5" strokeWidth={1.5} />
            </button>
            <p className="text-sm text-[#6B7280] mt-4">
              No credit card required • 5 feedback requests free per month
            </p>
          </div>
        </section>
      </main>

      <footer className="border-t border-gray-100 py-12 bg-white">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="text-[#8CAFA8]">
                  <Sprout className="w-6 h-6" strokeWidth={1.5} />
                </div>
                <span className="text-xl font-semibold text-[#1F2937]">Mentra</span>
              </div>
              <p className="text-[#6B7280] text-sm leading-relaxed">
                AI-powered career feedback for professionals and teams.
              </p>
            </div>

            <div>
              <h3 className="font-medium text-[#1F2937] mb-4">Product</h3>
              <ul className="space-y-2 text-[#6B7280] text-sm">
                <li><a href="/pricing" className="hover:text-[#1F2937] transition">Pricing</a></li>
                <li><a href="/features" className="hover:text-[#1F2937] transition">Features</a></li>
              </ul>
            </div>

            <div>
              <h3 className="font-medium text-[#1F2937] mb-4">Company</h3>
              <ul className="space-y-2 text-[#6B7280] text-sm">
                <li><a href="/about" className="hover:text-[#1F2937] transition">About</a></li>
              </ul>
            </div>

            <div>
              <h3 className="font-medium text-[#1F2937] mb-4">Legal</h3>
              <ul className="space-y-2 text-[#6B7280] text-sm">
                <li><a href="/privacy" className="hover:text-[#1F2937] transition">Privacy Policy</a></li>
                <li><a href="/terms" className="hover:text-[#1F2937] transition">Terms of Service</a></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-100 pt-8 text-center text-[#6B7280] text-sm">
            <p>&copy; 2025 Mentra. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {showDemo && <DemoModal onClose={() => setShowDemo(false)} />}
    </div>
  );
}
